import React, { useState, useEffect, useContext, useMemo } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom"; 
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { FaPlus, FaUser, FaEdit, FaTrash } from "react-icons/fa";
import { DataGridTable, SearchBar, FilterSelect, FiltersContainer } from "../components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import "../styles/common.css";
import "./Users.css";

export default function Users() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // État pour le zoom
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('users-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('users-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        
        const response = await api.get("/api/users/", { params });
        const usersData = Array.isArray(response.data) ? response.data : (response.data.results || []);
        setUsers(usersData);
        setLoading(false);
      } catch (err) {
        setError(t.errorLoadingUsers);
        setLoading(false);
      }
    };
    fetchUsers();
  }, [searchTerm]);

  // Fonction pour obtenir les noms des groupes
  const getGroupsNames = (user) => {
    if (user.groups_names && Array.isArray(user.groups_names)) {
      return user.groups_names.join(", ");
    }
    if (user.groups && Array.isArray(user.groups)) {
      // Si on a seulement les IDs, on ne peut pas afficher les noms sans charger les groupes
      return `${user.groups.length} groupe(s)`;
    }
    return "-";
  };

  // Préparer les données pour DataGrid
  const rows = useMemo(() => {
    return users.map(userItem => ({
      id: userItem.id,
      username: userItem.username,
      email: userItem.email || "-",
      first_name: userItem.first_name || "-",
      last_name: userItem.last_name || "-",
      groups: getGroupsNames(userItem),
    }));
  }, [users]);

  // Configuration des colonnes pour DataGrid
  const baseColumns = useMemo(() => [
    {
      field: 'username',
      headerName: t.username,
      width: 150,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'email',
      headerName: t.email,
      width: 200,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'first_name',
      headerName: t.firstName,
      width: 150,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'last_name',
      headerName: t.lastName,
      width: 150,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'groups',
      headerName: t.groups,
      width: 200,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
  ], [t]);

  const adminColumns = useMemo(() => {
    // Seul l'admin peut modifier/supprimer des utilisateurs
    if (!user?.is_admin) return [];
    
    return [
      {
        field: 'actions',
        headerName: t.actions,
        width: 180,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button
              onClick={() => navigate(`/users/${params.row.id}/modifier`)}
              className="grid-action-button"
              style={{ fontSize: `${1.1 * zoomLevel}rem` }}
              title={t.edit}
            >
              <FaEdit />
            </button>
            <button
              onClick={() => handleDelete(params.row.id)}
              className="grid-action-button grid-action-button-danger"
              style={{ fontSize: `${1.1 * zoomLevel}rem` }}
              title={t.delete}
            >
              <FaTrash />
            </button>
          </div>
        ),
      },
    ];
  }, [user, navigate, zoomLevel, t]);

  const columns = useMemo(() => {
    const hasActionColumns = adminColumns.length > 0;
    return hasActionColumns ? [...baseColumns, ...adminColumns] : baseColumns;
  }, [baseColumns, adminColumns]);

  const handleDelete = async (userId) => {
    if (!window.confirm(t.confirmDeleteUser)) {
      return;
    }

    try {
      await api.delete(`/api/users/${userId}/`);
      toast.success(t.userDeletedSuccess);
      // Recharger la liste
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      const response = await api.get("/api/users/", { params });
      const usersData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setUsers(usersData);
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          t.errorDeletingUser;
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="users-page">
        <div className="loading-container">
          <p className="loading-text">{t.loadingUsers}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="users-page">
        <div className="title-badge">
          <FaUser className="page-title-icon" aria-hidden="true" />
          <span>{t.error}</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="title-badge">
        <FaUser className="page-title-icon" aria-hidden="true" />
        <span>Gestion des Utilisateurs</span>
      </div>

      {/* Bouton d'action admin - Seul l'admin peut ajouter un utilisateur */}
      {user?.is_admin && (
        <div className="action-buttons-container">
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-user")}
          >
            <FaPlus />
            <span>Ajouter un utilisateur</span>
          </button>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="top-section">
        <SearchBar
          placeholder="Rechercher un utilisateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="default"
        />
      </div>

      {/* DataGrid */}
      {users.length === 0 ? (
        <div className="empty-message-container">
          <p className="empty-message">Aucun utilisateur enregistré.</p>
        </div>
      ) : (
        <DataGridTable
          rows={rows}
          columns={columns}
          loading={loading}
          storageKey="users-zoom-level"
          zoomLevel={zoomLevel}
          onZoomChange={setZoomLevel}
          initialSort={{ field: 'username', sort: 'asc' }}
          pageSize={25}
          containerClassName="users-grid-container"
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

